import { NgTemplateOutlet } from "@angular/common";
import { Component, computed, inject, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { firstOrDefault } from "@ankaadia/ankaadia-shared";
import { NewUiButton } from "@ankaadia/ankaadia-shared-frontend";
import { EmptystateComponent } from "@ankaadia/ui";
import { isEmpty, isNil } from "lodash";
import { Dialog } from "primeng/dialog";
import { Select } from "primeng/select";
import { ToggleSwitch } from "primeng/toggleswitch";
import { NewUiDropdownSortOnTopDirective } from "../../../shared/primeng/dropdown-sort-selected-value-on-top/new-ui-dropdown-sort-on-top.directive";
import { TranslateDirective } from "../../../shared/transloco/translate.directive";
import { NewUiCandidateFilterComponent } from "../../candidates/candidate-filter/new-ui-candidate-filter.component";
import { FilterMetadataMap } from "../../collections/collection-edit-assigned-candidates/custom-filter.model";
import { DashboardListOption } from "../../milestones/dashboard-list-option.model";
import { MilestoneViewService } from "../../milestones/milestone-view.service";
import { NewUiMilestoneAggregatedPlanningViewComponent } from "../../milestones/milestone-aggregated-planning-view/new-ui-milestone-aggregated-planning-view.component";
import { NewUiDashboardCardHeaderComponent } from "../layout/new-ui-dashboard-card-header/new-ui-dashboard-card-header.component";
import { NewUiDashboardCardComponent } from "../layout/new-ui-dashboard-card/new-ui-dashboard-card.component";

@Component({
  selector: "new-ui-dashboard-milestone-planning",
  templateUrl: "./new-ui-dashboard-milestone-planning.component.html",
  imports: [
    NewUiDashboardCardHeaderComponent,
    TranslateDirective,
    NewUiDashboardCardComponent,
    NewUiMilestoneAggregatedPlanningViewComponent,
    EmptystateComponent,
    Select,
    NewUiDropdownSortOnTopDirective,
    NewUiButton,
    Dialog,
    NewUiCandidateFilterComponent,
    FormsModule,
    NgTemplateOutlet,
    ToggleSwitch,
  ],
})
export class NewUiDashboardMilestonePlanningComponent implements OnInit {
  private readonly milestoneViewService = inject(MilestoneViewService);

  protected readonly isNil = isNil;

  protected candidateSum = 0;
  protected fullscreen = false;

  protected availableDashboardLists: DashboardListOption[] = [];
  private areDashboardListsLoaded = false;
  protected readonly selectedDashboardList = signal<DashboardListOption | null>(null);

  protected candidateFilter: FilterMetadataMap = {};

  // The schedule node's outer/inner ring, shared by the card and the fullscreen dialog
  protected readonly showProgress = signal<boolean>(true);
  protected readonly showPlanningData = signal<boolean>(true);

  // A list shared by another organization shows only what its sharing lets through, and offers no toggle
  // for the rest; an own list shows everything
  private readonly dashboardSharing = computed(() => this.selectedDashboardList()?.dashboardSharing ?? null);
  protected readonly canShowProgress = computed(
    () => isNil(this.dashboardSharing()) || this.dashboardSharing().showProgress
  );

  protected readonly canShowPlanningData = computed(
    () => isNil(this.dashboardSharing()) || this.dashboardSharing().showPlanningData
  );

  protected readonly isProgressShown = computed(() => this.canShowProgress() && this.showProgress());
  protected readonly isPlanningDataShown = computed(() => this.canShowPlanningData() && this.showPlanningData());

  // Per timeline, since the card's and the fullscreen dialog's each fetch on their own
  protected readonly isCardLoading = signal(false);
  protected readonly isDialogLoading = signal(false);

  protected readonly isSummaryShown = computed(
    () => isNil(this.dashboardSharing()) || this.dashboardSharing().showSummary
  );

  // No list shared to the dashboard at all, or the selected one has no milestones - the timeline reports the
  // latter as a null candidate sum
  protected get isEmptyStateShown(): boolean {
    const hasNoLists = this.areDashboardListsLoaded && isEmpty(this.availableDashboardLists);
    return hasNoLists || this.candidateSum === null;
  }

  onCandidateFilterChanged(filter: FilterMetadataMap): void {
    this.candidateFilter = filter;
  }

  ngOnInit(): void {
    this.milestoneViewService.fetchSupportedDashboardLists("no-cache").subscribe((lists) => {
      this.availableDashboardLists = lists ?? [];
      this.areDashboardListsLoaded = true;
      this.selectedDashboardList.set(firstOrDefault(this.availableDashboardLists));
    });
  }

  onDashboardListSelected(listId: string): void {
    this.selectedDashboardList.set(this.availableDashboardLists.find((list) => list.id === listId) ?? null);
    // The empty state replaced the timeline, so only a fresh one can tell whether this list has milestones
    this.candidateSum = 0;
  }

  onCandidateSumChange(sum: number): void {
    this.candidateSum = sum;
  }
}
